import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Crown, Star } from 'lucide-react';
import Button from "../ui/button";

const segments = [
  {
    tier: 'Basic',
    icon: Check,
    tagline: 'Smart & Functional',
    description: 'Perfect for those who want quality design without breaking the bank.',
    features: [
      'Smart & functional layouts',
      'Budget-friendly designs',
      'Essential planning & execution',
      '2D floor plans included',
      'Basic material selection',
      'Standard timeline delivery',
    ],
    image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&q=80',
    accent: '#7C9082',
  },
  {
    tier: 'Premium',
    icon: Star,
    tagline: 'Modern Aesthetics',
    description: 'Elevated designs with custom touches and premium materials.',
    features: [
      'Modern aesthetic designs',
      'Custom layouts & lighting',
      'High-quality materials',
      '3D visualization included',
      'Dedicated project manager',
      'Priority timeline delivery',
    ],
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80',
    accent: '#C9A962',
    popular: true,
  },
  {
    tier: 'Luxury',
    icon: Crown,
    tagline: 'Bespoke Excellence',
    description: 'Exclusive, personalized design for the most discerning clients.',
    features: [
      'Bespoke interior concepts',
      'High-end finishes & detailing',
      'Exclusive design consultation',
      'Statement lighting & furniture',
      'Premium imported materials',
      'VIP concierge service',
    ],
    image: 'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=600&q=80',
    accent: '#8B7355',
  },
];

export default function Segments() {
  return (
    <section className="py-24 md:py-32 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#C9A962]" />
            <span className="text-[#C9A962] tracking-[0.3em] text-xs uppercase">Tailored Solutions</span>
            <div className="w-12 h-[1px] bg-[#C9A962]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C2C2C] mb-4">
            Design Solutions for Every Segment
          </h2>
          <p className="text-[#2C2C2C]/60 max-w-2xl mx-auto text-lg">
            Whether you're looking for smart functionality or ultimate luxury, we have the perfect package for you.
          </p>
        </motion.div>

        {/* Segments Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {segments.map((segment, index) => (
            <motion.div
              key={segment.tier}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className={`relative group bg-white border overflow-hidden ${
                segment.popular 
                  ? 'border-[#C9A962] shadow-xl scale-105 z-10' 
                  : 'border-[#E8E2D9] hover:border-[#C9A962]/50'
              } transition-all duration-500`}
            >
              {/* Popular Badge */}
              {segment.popular && (
                <div className="absolute top-4 right-4 z-20 bg-[#C9A962] text-white text-xs px-3 py-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Most Popular
                </div>
              )}

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={segment.image}
                  alt={segment.tier}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C] to-transparent" />
                <div className="absolute bottom-4 left-6">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: segment.accent }}
                  >
                    <segment.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-serif text-2xl text-white">{segment.tier}</h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-sm uppercase tracking-widest mb-2" style={{ color: segment.accent }}>
                  {segment.tagline}
                </p>
                <p className="text-[#2C2C2C]/60 text-sm mb-6">
                  {segment.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {segment.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#2C2C2C]/80">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: segment.accent }} />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className={`w-full rounded-none py-6 ${
                    segment.popular
                      ? 'bg-[#C9A962] hover:bg-[#b8954f] text-white'
                      : 'bg-transparent border border-[#2C2C2C] text-[#2C2C2C] hover:bg-[#2C2C2C] hover:text-white'
                  }`}
                >
                  Get Started
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}